import React, { useEffect, useMemo, useState } from 'react';
import { sendOrderlessOrder, listOrderlessOrderParties } from '../services/stockAnalysisApi';
import { whatsappNumber } from '../utils/cardParser';
import { buildOrderImageBlob } from '../utils/orderlessPdf';
import { formatDate } from '../utils/dateFormat';

/** Stable identity of one Orderless Stock detail row (one design + size + batch + pack). */
export function orderlessOrderKey(r) {
  return [r.design_name, r.size_name, r.batch_shade, r.box_pack].join('|');
}

/** A detail row -> an order line (default qty = everything available). Rows with no stock can't be ordered. */
export function makeOrderLine(r) {
  const max = Math.floor(Number(r.qty) || 0);
  if (max <= 0) return null;
  return {
    key: orderlessOrderKey(r),
    design_name: r.design_name,
    batch_shade: r.batch_shade || '',
    box_pack: r.box_pack || '',
    size_name: r.size_name || '',
    maxQty: max,
    qty: max,
    weight: r.weight !== null && r.weight !== undefined && Number.isFinite(Number(r.weight)) ? Number(r.weight) : null,
  };
}

const BORDER = '1px solid var(--color-border)';
const thS = { padding: '8px 4px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#1e293b', borderRight: BORDER, textAlign: 'left' };
const tdS = { padding: '6px 4px', fontSize: 'var(--font-size-sm)', borderRight: BORDER };
const inputS = { padding: '6px 8px', fontSize: 'var(--font-size-sm)', border: BORDER, borderRadius: 4, background: '#fff', color: '#1e293b', boxSizing: 'border-box' };
const labelS = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' };
const btnS = { padding: '8px 14px', fontSize: 'var(--font-size-sm)', border: BORDER, borderRadius: 6, background: 'var(--color-surface)', color: '#1e293b', cursor: 'pointer' };
const btnPrimary = { ...btnS, background: 'var(--color-accent)', color: '#fff', borderColor: 'var(--color-accent)' };

function r1(n) { return Math.round(n * 10) / 10; }
function fmt(n) { return Number(n).toLocaleString('en-IN'); }
function readStore(k) { try { return window.localStorage.getItem(k) || ''; } catch (e) { return ''; } }
function writeStore(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* storage unavailable */ } }

function orderText(partyName, lines, totals) {
  // WhatsApp-style formatting (*bold*), same look as the Price List share text.
  const out = ['*ORDER ENQUIRY - STOCK LIST*'];
  const meta = [];
  if (partyName) meta.push(`To: ${partyName}`);
  meta.push(`Date: ${formatDate(new Date())}`);
  out.push(meta.join(' | '));
  const sizes = [];
  lines.forEach((l) => { const sz = l.size_name || '-'; if (!sizes.includes(sz)) sizes.push(sz); });
  let n = 0;
  sizes.forEach((sz) => {
    out.push('');
    out.push(`*Size: ${sz}*`);
    lines.filter((l) => (l.size_name || '-') === sz).forEach((l) => {
      n += 1;
      out.push(`${n}. *${l.design_name}*`);
      const det = [];
      if (l.batch_shade) det.push(`Batch/Shade: ${l.batch_shade}`);
      if (l.box_pack) det.push(`Pack: ${l.box_pack}`);
      det.push(`Qty: ${fmt(l.qty)} boxes`);
      if (l.weight !== null) det.push(`Wt: ${fmt(r1(l.weight * Number(l.qty)))} Kg`);
      out.push(`    ${det.join(' | ')}`);
    });
  });
  out.push('');
  out.push('*Grand Total*');
  out.push(`Designs: ${totals.designs} | Boxes: ${fmt(totals.qty)} | Weight: ${fmt(totals.weight)} Kg`);
  out.push('');
  out.push('Kindly confirm your order. Thank you!');
  return out.join('\n');
}

export default function OrderlessOrderBuilder({ lines, onQtyChange, onRemove, onClear }) {
  const [parties, setParties] = useState([]);
  const [partyName, setPartyName] = useState('');
  const [note, setNote] = useState('');
  const [myChatId, setMyChatId] = useState(() => readStore('orderlessMyChatId'));
  const [email, setEmail] = useState(() => readStore('orderlessEmail'));
  const [waMobile, setWaMobile] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let alive = true;
    listOrderlessOrderParties().then((items) => { if (alive) setParties(items || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const matchedParty = useMemo(() => {
    const q = partyName.trim().toLowerCase();
    return q ? parties.find((p) => p.name.trim().toLowerCase() === q) : undefined;
  }, [parties, partyName]);

  const valid = lines.filter((l) => Number(l.qty) > 0);
  const totals = useMemo(() => ({
    designs: new Set(valid.map((l) => l.design_name)).size,
    qty: valid.reduce((t, l) => t + Number(l.qty), 0),
    weight: r1(valid.reduce((t, l) => t + (l.weight !== null ? l.weight * Number(l.qty) : 0), 0)),
  }), [valid]);

  if (!lines || lines.length === 0) return null;

  const send = async (channels) => {
    setBusy(true); setResults(null); setMessage('');
    if (channels.includes('myTelegram')) writeStore('orderlessMyChatId', myChatId.trim());
    if (channels.includes('email')) writeStore('orderlessEmail', email.trim());
    try {
      const data = await sendOrderlessOrder({
        partyName: partyName.trim(),
        note: note.trim(),
        lines: valid,
        channels,
        telegramChatId: myChatId.trim(),
        refPartyId: matchedParty ? matchedParty.id : undefined,
        email: email.trim(),
      });
      setResults(data.results || []);
    } catch (err) {
      const apiMsg = err && err.response && err.response.data && err.response.data.error;
      setMessage(typeof apiMsg === 'string' ? apiMsg : 'Could not send. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    setResults(null);
    try {
      await navigator.clipboard.writeText(orderText(partyName.trim(), valid, totals));
      setMessage('Order copied.');
    } catch (e) {
      setMessage('Copy not allowed in this browser.');
    }
  };

  // The party's WhatsApp number is remembered in this browser, per party name.
  const waKey = (name) => `orderlessWa:${String(name || '').trim().toLowerCase()}`;
  const onPartyChange = (v) => {
    setPartyName(v);
    setWaMobile(readStore(waKey(v)));
  };

  // Opens WhatsApp (wa.me click-to-chat) with the order text; the user presses send there.
  const sendWhatsApp = () => {
    setResults(null);
    const num = whatsappNumber(waMobile);
    if (!num) { setMessage('Enter a valid WhatsApp mobile number for this customer.'); return; }
    if (partyName.trim()) writeStore(waKey(partyName), waMobile.trim());
    const text = orderText(partyName.trim(), valid, totals) + (note.trim() ? `\nNote: ${note.trim()}` : '');
    setMessage('WhatsApp opened - check the message and press send.');
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };

  // Makes the order as ONE image, copies it to the clipboard and opens the CUSTOMER's WhatsApp chat.
  // WhatsApp cannot be pre-filled with a file, so the user just pastes (Ctrl+V) and presses send.
  const sendWhatsAppPdf = async () => {
    setResults(null);
    const num = whatsappNumber(waMobile);
    if (!num) { setMessage('Enter a valid WhatsApp mobile number for this customer.'); return; }
    if (partyName.trim()) writeStore(waKey(partyName), waMobile.trim());
    setPdfBusy(true);
    setMessage('');
    // FOCUSFIX: copy first (page must be focused), open the chat tab afterwards.
    try {
      const blobPromise = buildOrderImageBlob(partyName.trim(), valid, totals, note.trim());
      let copied = false;
      try {
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blobPromise })]);
        copied = true;
      } catch (e) {
        copied = false;
      }
      const blob = await blobPromise;
      if (!copied) {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `Order_${new Date().toISOString().slice(0, 10)}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // WAJUMP: no wa.me/api.whatsapp.com middle page. Phone -> app deep link in this tab (stay on Stock Analysis);
      // computer -> straight WhatsApp Web chat in one reusable tab.
      if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        window.location.href = `whatsapp://send?phone=${num}`;
      } else if (!window.open(`https://web.whatsapp.com/send?phone=${num}`, 'wa_web')) {
        window.location.href = `https://web.whatsapp.com/send?phone=${num}`;
      }
      setMessage(copied
        ? 'Order image copied. In the customer chat press Ctrl+V (mobile: long-press the message box > Paste), then send.'
        : 'Order image downloaded. In the customer chat tap the attach (clip) icon and pick the image.');
    } catch (err) {
      setMessage('Could not prepare the image. Please try again.');
    } finally {
      setPdfBusy(false);
    }
  };

  const goToList = () => {
    const el = document.getElementById('orderless-order-list');
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <div
        style={{ position: 'fixed', left: 16, bottom: 16, zIndex: 50, display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', background: '#1e293b', color: '#fff', borderRadius: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.3)', fontSize: 'var(--font-size-sm)' }}
      >
        <span>{`Order: ${valid.length} line(s), ${fmt(totals.qty)} boxes`}</span>
        <button type="button" onClick={goToList} style={{ ...btnS, padding: '4px 10px', background: '#fff' }}>View order list</button>
      </div>

      <div id="orderless-order-list" style={{ background: 'var(--color-surface)', border: BORDER, borderRadius: 8, padding: 'var(--space-4)', margin: 'var(--space-3) 0' }}>
        <h3 style={{ margin: '0 0 var(--space-3)' }}>Order List</h3>

        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
          <label style={labelS}>
            Party / Customer
            <input list="orderless-order-parties" value={partyName} onChange={(e) => onPartyChange(e.target.value)} placeholder="Type or pick a party" style={{ ...inputS, width: 260 }} />
            <datalist id="orderless-order-parties">
              {parties.map((p) => <option key={p.id} value={p.name} />)}
            </datalist>
          </label>
          <label style={labelS}>
            Note (optional)
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. delivery instruction" style={{ ...inputS, width: 260 }} />
          </label>
        </div>

        <div className="table-scroll">
          <table style={{ width: '100%', borderCollapse: 'collapse', border: BORDER }}>
            <thead>
              <tr style={{ background: '#e2e8f0', borderBottom: BORDER }}>
                <th style={thS}>#</th>
                <th style={thS}>{`Design (${totals.designs})`}</th>
                <th style={thS}>Batch/Shade</th>
                <th style={thS}>BoxPack</th>
                <th style={thS}>Size</th>
                <th className="data-figure" style={thS}>Qty</th>
                <th className="data-figure" style={thS}>Weight/Box</th>
                <th className="data-figure" style={thS}>Total Weight</th>
                <th style={{ ...thS, borderRight: 'none' }} />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.key} style={{ borderBottom: BORDER }}>
                  <td style={tdS}>{i + 1}</td>
                  <td style={tdS}>{l.design_name}</td>
                  <td style={tdS}>{l.batch_shade || '—'}</td>
                  <td style={tdS}>{l.box_pack || '—'}</td>
                  <td style={tdS}>{l.size_name || '—'}</td>
                  <td className="data-figure" style={tdS}>
                    <input
                      type="number"
                      min="0"
                      max={l.maxQty}
                      value={l.qty}
                      title={`Available: ${fmt(l.maxQty)}`}
                      onChange={(e) => onQtyChange(l.key, e.target.value)}
                      style={{ ...inputS, width: 90, padding: '3px 6px', textAlign: 'right' }}
                    />
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginLeft: 4 }}>{`/ ${fmt(l.maxQty)}`}</span>
                  </td>
                  <td className="data-figure" style={tdS}>{l.weight !== null ? l.weight : '—'}</td>
                  <td className="data-figure" style={tdS}>{l.weight !== null ? r1(l.weight * (Number(l.qty) || 0)) : '—'}</td>
                  <td style={{ ...tdS, borderRight: 'none', textAlign: 'center' }}>
                    <button type="button" aria-label="Remove line" onClick={() => onRemove(l.key)} style={{ ...btnS, padding: '2px 8px' }}>✕</button>
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#e2e8f0', borderTop: '2px solid var(--color-border)' }}>
                <td colSpan={5} style={{ ...thS, textAlign: 'right' }}>Grand Total</td>
                <td className="data-figure" style={thS}>{fmt(totals.qty)}</td>
                <td style={thS} />
                <td className="data-figure" style={thS}>{fmt(totals.weight)}</td>
                <td style={{ ...thS, borderRight: 'none' }} />
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', margin: 'var(--space-3) 0', alignItems: 'flex-end' }}>
          <label style={labelS}>
            My Telegram chat id
            <input value={myChatId} onChange={(e) => setMyChatId(e.target.value)} placeholder="e.g. 123456789" style={{ ...inputS, width: 180 }} />
          </label>
          <button type="button" disabled={busy || valid.length === 0} onClick={() => send(['myTelegram'])} style={btnPrimary}>Send to my Telegram</button>
          <button
            type="button"
            disabled={busy || valid.length === 0 || !matchedParty || !matchedParty.hasTelegram}
            title={!matchedParty ? 'Pick a party from the list' : (!matchedParty.hasTelegram ? 'This party has no Telegram chat id' : '')}
            onClick={() => send(['partyTelegram'])}
            style={btnS}
          >
            Send to party Telegram
          </button>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-3)', alignItems: 'flex-end' }}>
          <label style={labelS}>
            Email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" style={{ ...inputS, width: 260 }} />
          </label>
          <button type="button" disabled={busy || valid.length === 0} onClick={() => send(['email'])} style={btnS}>Send Email</button>
          <button type="button" disabled={valid.length === 0} onClick={copy} style={btnS}>Copy text</button>
          <button type="button" disabled={busy} onClick={onClear} style={btnS}>Clear order</button>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-3)', alignItems: 'flex-end' }}>
          <label style={labelS}>
            Customer WhatsApp mobile
            <input type="tel" value={waMobile} onChange={(e) => setWaMobile(e.target.value)} placeholder="e.g. 98XXXXXXXX" style={{ ...inputS, width: 180 }} />
          </label>
          <button type="button" disabled={valid.length === 0 || pdfBusy} onClick={sendWhatsAppPdf} style={btnPrimary}>{pdfBusy ? 'Making image…' : 'Send Image on WhatsApp'}</button>
          <button type="button" disabled={valid.length === 0} onClick={sendWhatsApp} style={btnS}>Send text on WhatsApp</button>
        </div>

        {busy && <p style={{ color: 'var(--color-text-secondary)' }}>Sending…</p>}
        {message && <p style={{ color: 'var(--color-text-secondary)' }}>{message}</p>}
        {results && results.map((r) => (
          <p key={r.channel} style={{ margin: '2px 0', color: r.sent ? 'var(--color-success)' : 'var(--color-danger)' }}>
            {r.sent ? `✔ Sent — ${r.label}` : `✖ ${r.label}: ${r.reason || 'failed'}`}
          </p>
        ))}
      </div>
    </>
  );
}
